import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Check, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMembership } from '../../hooks/useMembership';
import { MEMBERSHIP_TYPES, FREE_LIMITS, PLUS_LIMITS } from '../../utils/membershipConstants';

/**
 * UpgradeModal Component
 * Notes+ - Bosph Technologies
 *
 * Free kullanıcılar için Plus üyeliğe yükseltme modal'ı
 */
const UpgradeModal = ({ isOpen, onClose, trigger = 'manual' }) => {
  const { t } = useTranslation('shared');
  const { membershipType, limits, isFreeUser } = useMembership();

  if (!isFreeUser || membershipType !== MEMBERSHIP_TYPES.FREE) {
    return null;
  }

  const features = [
    {
      title: t('upgradeModal.features.notes.title'),
      free: t('upgradeModal.features.notes.free', { count: FREE_LIMITS.maxNoteCount }),
      plus: t('upgradeModal.features.notes.plus'),
      icon: '📝',
    },
    {
      title: t('upgradeModal.features.themes.title'),
      free: 'Light & Dark',
      plus: 'Light, Dark & Aurora',
      icon: '🎨',
    },
    {
      title: t('upgradeModal.features.calendar.title'),
      free: t('upgradeModal.features.calendar.free'),
      plus: t('upgradeModal.features.calendar.plus'),
      icon: '📅',
    },
    {
      title: t('upgradeModal.features.recurringEvents.title'),
      free: '❌',
      plus: '✅',
      icon: '🔄',
    },
    {
      title: t('upgradeModal.features.media.title'),
      free: '❌',
      plus: '✅',
      icon: '🖼️',
    },
    {
      title: t('upgradeModal.features.sounds.title'),
      free: '❌',
      plus: '✅',
      icon: '🔊',
    },
    {
      title: t('upgradeModal.features.sync.title'),
      free: '❌',
      plus: '✅',
      icon: '☁️',
    },
    {
      title: t('upgradeModal.features.adFree.title'),
      free: '❌',
      plus: '✅',
      icon: '🚫',
    },
  ];

  const handleUpgrade = () => {
    // Payment integration will be added in a future version
    // For now, show information message
    alert(t('upgradeModal.comingSoonAlert'));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-4xl border border-white/10 dark:border-white/10 my-8 relative overflow-hidden">
              {/* Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#3d8ef0]/10 via-purple-500/10 to-pink-500/10 dark:from-[#3d8ef0]/15 dark:via-purple-600/20 dark:to-pink-600/20" />
              
              <div className="relative">
                {/* Header */}
                <div className="p-6 border-b border-white/10 dark:border-white/10 relative">
                  <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] transition-colors z-10"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Crown className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-[var(--color-text-primary)] dark:text-white flex items-center space-x-2">
                        <span>{t('upgradeModal.title')}</span>
                        <Sparkles className="w-6 h-6 text-[#3d8ef0]" />
                      </h2>
                      <p className="text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mt-1">
                        {t('upgradeModal.subtitle')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Plan Comparison */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-1"></div>
                    <div className="text-center">
                      <div className="px-4 py-2 bg-[var(--color-bg-elevated)] dark:bg-[#161f31] rounded-lg">
                        <p className="text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300">{t('upgradeModal.standardMember')}</p>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="px-4 py-2 bg-gradient-to-r from-primary-500 to-purple-600 rounded-lg text-white">
                        <p className="text-sm font-medium">{t('upgradeModal.plus')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-3">
                    {features.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="grid grid-cols-3 gap-4 items-center p-4 bg-[var(--color-bg-muted)] dark:bg-[#161f31] rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{feature.icon}</span>
                          <span className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white">
                            {feature.title}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="text-sm text-[var(--color-text-secondary)] dark:text-[var(--color-text-muted)]">{feature.free}</span>
                        </div>
                        <div className="text-center">
                          <span className="text-sm font-medium text-[#3d8ef0] dark:text-[#7ab8f8]">
                            {feature.plus}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* CTA Section */}
                  <div className="pt-6 border-t border-white/10 dark:border-white/10">
                    <div className="bg-gradient-to-r from-primary-500 to-purple-600 rounded-xl p-6 text-center">
                      <h3 className="text-2xl font-bold text-white mb-2">{t('upgradeModal.ctaTitle')}</h3>
                      <p className="text-white/90 mb-6">
                        {t('upgradeModal.ctaSubtitle')}
                      </p>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleUpgrade}
                        className="px-8 py-3 bg-[var(--color-bg-base)] text-[#3d8ef0] rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 mx-auto"
                      >
                        <Crown className="w-5 h-5" />
                        <span>{t('upgradeModal.upgradeButton')}</span>
                      </motion.button>
                      <p className="text-white/70 text-xs mt-4">
                        {t('upgradeModal.paymentComingSoon')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UpgradeModal;








